import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { LEGAL_DOC_ORDER, PAYMENTS_ENABLED, type LegalDocId } from '@redevicer/shared'
import SellerOnboardingWizard from './SellerOnboardingWizard'
import CollapseSlot from './CollapseSlot'
import {
  IconAccountCamera,
  IconAccountDeals,
  IconAccountHelp,
  IconAccountListings,
  IconAccountSearch,
  IconAccountSettings,
} from './AccountIcons'
import { paymentsAPI, sellerVerificationAPI, stripeConnectAPI, uploadAPI, authAPI } from '../api/client'
import SellerCitiesPicker from './SellerCitiesPicker'
import LanguageSelect from './LanguageSelect'
import { IconReport } from './AppNavIcons'
import { publicAssetUrl } from '../utils/assetUrl'
import {
  isPromoBannerEnabled,
  setPromoBannerEnabled,
} from '../utils/promoBannerPrefs'

interface StoredUser {
  id?: number
  email?: string
  full_name?: string
  avatar_url?: string | null
  password_login_enabled?: boolean
}

interface AccountPageProps {
  token: string | null
  userJson: string | null
  isAdmin: boolean
  onBrowse: () => void
  onExamples: () => void
  onMyListings: () => void
  onMyOffers: () => void
  onMyChats: () => void
  onMyReports: () => void
  onAdmin: () => void
  onLogout: () => void
  onLogin?: () => void
  onSell?: () => void
  onUserUpdated?: (user: StoredUser) => void
  language: string
  onLanguage: (lng: string) => void
  onOpenLegal: (docId: LegalDocId) => void
  focusSellerVerify?: boolean
  focusProfileCities?: boolean
  onFocusProfileCitiesHandled?: () => void
  listingsCount?: number
}

interface ConnectStatus {
  stripe_enabled: boolean
  has_account: boolean
  charges_enabled: boolean
  payouts_enabled: boolean
}

type AccountTab = 'actions' | 'selling' | 'settings'

type QuickAction = {
  key: string
  label: string
  Icon: typeof IconAccountSearch
  onClick?: () => void
}

function parseUser(raw: string | null): StoredUser | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as StoredUser
  } catch {
    return null
  }
}

function LegalAccordion({ onOpenLegal }: { onOpenLegal: (id: LegalDocId) => void }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div className="vc-account-legal">
      <button
        type="button"
        className="vc-account-row"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="vc-account-row__text">{t('vc.profile.legal')}</span>
        <span className="vc-account-row__chevron" aria-hidden="true">
          {open ? '⌃' : '›'}
        </span>
      </button>
      <CollapseSlot open={open}>
        <div className="vc-account-legal-list">
          {LEGAL_DOC_ORDER.map((docId) => (
            <button
              key={docId}
              type="button"
              className="vc-account-legal-item"
              onClick={() => onOpenLegal(docId)}
            >
              {t(`legal.doc.${docId}`)}
            </button>
          ))}
        </div>
      </CollapseSlot>
    </div>
  )
}

export default function AccountPage({
  token,
  userJson,
  isAdmin,
  onBrowse,
  onExamples,
  onMyListings,
  onMyOffers,
  onMyChats,
  onMyReports,
  onAdmin,
  onLogout,
  onLogin,
  onSell,
  language,
  onLanguage,
  onOpenLegal,
  focusSellerVerify = false,
  focusProfileCities = false,
  onFocusProfileCitiesHandled,
  listingsCount = 0,
  onUserUpdated,
}: AccountPageProps) {
  const { t } = useTranslation()
  const verifySectionRef = useRef<HTMLDivElement>(null)
  const profileCitiesRef = useRef<HTMLDivElement>(null)
  const user = parseUser(userJson)
  const isGuest = !token

  const [tab, setTab] = useState<AccountTab>('actions')
  const [connect, setConnect] = useState<ConnectStatus | null>(null)
  const [connectLoading, setConnectLoading] = useState(() => Boolean(token))
  const [exchangePassActive, setExchangePassActive] = useState<boolean | null>(null)
  const [verifyDone, setVerifyDone] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [promoBannerEnabled, setPromoBannerEnabledState] = useState(isPromoBannerEnabled)
  const [wizardOpen, setWizardOpen] = useState(false)
  const [wizardStep, setWizardStep] = useState(0)
  const [editingProfile, setEditingProfile] = useState(false)
  const [editName, setEditName] = useState(user?.full_name ?? '')
  const [editPhone, setEditPhone] = useState('')
  const [editCities, setEditCities] = useState<string[]>([])
  const [cityOptions, setCityOptions] = useState<string[]>([])
  const [profileBusy, setProfileBusy] = useState(false)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [avatarTs, setAvatarTs] = useState(0)
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState<boolean | null>(null)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordBusy, setPasswordBusy] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const display = user?.full_name || user?.email || t('vc.account.guest')
  const initial = (display[0] ?? '?').toUpperCase()
  const avatarSrc = user?.avatar_url
    ? `${publicAssetUrl(user.avatar_url)}${avatarTs ? `?v=${avatarTs}` : ''}`
    : null

  async function handleAvatarChange(file: File | undefined) {
    if (!token || !file) return
    setAvatarBusy(true)
    try {
      await uploadAPI.uploadAvatar(file, token)
      const me = await authAPI.getCurrentUser(token)
      const updated = me.data as StoredUser
      localStorage.setItem('user', JSON.stringify(updated))
      onUserUpdated?.(updated)
      setAvatarTs(Date.now())
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(msg || t('account.avatarFailed'))
    } finally {
      setAvatarBusy(false)
      if (avatarInputRef.current) avatarInputRef.current.value = ''
    }
  }

  useEffect(() => {
    sellerVerificationAPI
      .getCities()
      .then((res) => setCityOptions((res.data?.cities as string[]) ?? []))
      .catch(() => setCityOptions([]))
  }, [])

  const loadStatus = useCallback(() => {
    if (!token) return
    setConnectLoading(true)
    Promise.all([
      sellerVerificationAPI.getStatus(token),
      ...(PAYMENTS_ENABLED
        ? [stripeConnectAPI.getStatus(token), paymentsAPI.exchangePassStatus(token)]
        : []),
    ])
      .then((results) => {
        const v = results[0]
        setVerifyDone(!!v.data?.can_sell)
        if (PAYMENTS_ENABLED && results.length > 1) {
          setConnect(results[1].data as ConnectStatus)
          setExchangePassActive(!!results[2].data?.active)
        } else {
          setConnect(null)
          setExchangePassActive(null)
        }
        if (v.data?.contact_phone) setEditPhone(String(v.data.contact_phone))
        if (Array.isArray(v.data?.seller_cities)) setEditCities(v.data.seller_cities as string[])
      })
      .catch(() => {
        setVerifyDone(false)
        setConnect(null)
        setExchangePassActive(false)
      })
      .finally(() => setConnectLoading(false))
  }, [token])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  useEffect(() => {
    if (!focusSellerVerify) return
    setTab('selling')
    setWizardOpen(true)
    const el = verifySectionRef.current
    if (!el) return
    const tmr = window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
    return () => window.clearTimeout(tmr)
  }, [focusSellerVerify])

  useEffect(() => {
    if (!focusProfileCities || isGuest) return
    setEditingProfile(true)
    const tmr = window.setTimeout(() => {
      profileCitiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      onFocusProfileCitiesHandled?.()
    }, 200)
    return () => window.clearTimeout(tmr)
  }, [focusProfileCities, isGuest, onFocusProfileCitiesHandled])

  useEffect(() => {
    setEditName(user?.full_name ?? '')
  }, [user?.full_name])

  useEffect(() => {
    if (!token) {
      setPasswordLoginEnabled(null)
      return
    }
    authAPI
      .getCurrentUser(token)
      .then((res) => {
        const enabled = (res.data as StoredUser)?.password_login_enabled
        setPasswordLoginEnabled(enabled !== false)
      })
      .catch(() => setPasswordLoginEnabled(null))
  }, [token, userJson])

  async function handleSetPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!token) return
    setPasswordError('')
    setPasswordMessage('')
    if (newPassword.length < 8) {
      setPasswordError(t('account.passwordTooShort'))
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('auth.resetPasswordMismatch'))
      return
    }
    setPasswordBusy(true)
    try {
      await authAPI.setPassword(token, {
        new_password: newPassword,
        ...(passwordLoginEnabled && currentPassword.trim()
          ? { current_password: currentPassword }
          : {}),
      })
      setPasswordLoginEnabled(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordMessage(
        passwordLoginEnabled ? t('account.passwordChanged') : t('account.passwordCreated'),
      )
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setPasswordError(
        typeof detail === 'string' && /invalid credentials/i.test(detail)
          ? t('account.passwordCurrentWrong')
          : typeof detail === 'string'
            ? detail
            : t('account.passwordChangeFailed'),
      )
    } finally {
      setPasswordBusy(false)
    }
  }

  async function saveProfileEdit() {
    if (!token) return
    setProfileBusy(true)
    try {
      await sellerVerificationAPI.saveProfile(token, {
        seller_type: 'individual',
        full_name: editName.trim() || undefined,
        contact_phone: editPhone.trim() || undefined,
        seller_cities: editCities,
      })
      setEditingProfile(false)
      loadStatus()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(msg || t('sellerVerify.submitFailed'))
    } finally {
      setProfileBusy(false)
    }
  }

  const stripeDone = PAYMENTS_ENABLED && !!connect?.charges_enabled
  const passDone = PAYMENTS_ENABLED && exchangePassActive === true
  const setupDone = PAYMENTS_ENABLED
    ? [verifyDone, stripeDone, passDone].filter(Boolean).length
    : verifyDone
      ? 1
      : 0
  const setupTotal = PAYMENTS_ENABLED ? 3 : 1

  const buyingActions: QuickAction[] = [
    { key: 'browse', label: t('menu.browse'), Icon: IconAccountSearch, onClick: onBrowse },
    { key: 'offers', label: t('vc.profile.ordersOffers'), Icon: IconAccountDeals, onClick: onMyOffers },
    { key: 'chats', label: t('offerChats.title'), Icon: IconAccountDeals, onClick: onMyChats },
    { key: 'reports', label: t('account.tabReports'), Icon: IconReport, onClick: onMyReports },
  ]

  const sellingActions: QuickAction[] = [
    { key: 'listings', label: t('menu.myListings'), Icon: IconAccountListings, onClick: onMyListings },
    { key: 'help', label: t('menu.examples'), Icon: IconAccountHelp, onClick: onExamples },
  ]

  const renderQuick = useCallback(
    (items: QuickAction[]) => (
      <div className="vc-account-quick-grid">
        {items.map((item) => {
          const { Icon } = item
          return (
            <button key={item.key} type="button" className="vc-account-quick" onClick={item.onClick} disabled={isGuest}>
              <span className="vc-account-quick__icon" aria-hidden="true">
                <Icon width={22} height={22} />
              </span>
              <span className="vc-account-quick__label">{item.label}</span>
            </button>
          )
        })}
      </div>
    ),
    [isGuest],
  )

  return (
    <section className="vc-account-page vc-account-page--modern">
      <h2 className="vc-account-page-title">{t('vc.tab.account')}</h2>

      <div className="vc-account-hero-card vc-account-span-full">
        {!isGuest ? (
          <label className="vc-account-avatar-wrap" title={t('account.avatarChange')}>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/*"
              className="vc-account-avatar-input"
              disabled={avatarBusy}
              onChange={(e) => void handleAvatarChange(e.target.files?.[0])}
            />
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="vc-account-avatar-img" />
            ) : (
              <div className="vc-account-avatar" aria-hidden="true">
                {initial}
              </div>
            )}
            <span className="vc-account-avatar-edit" aria-hidden="true">
              {avatarBusy ? '…' : <IconAccountCamera width={14} height={14} />}
            </span>
          </label>
        ) : (
          <div className="vc-account-avatar" aria-hidden="true">
            {initial}
          </div>
        )}
        <div className="vc-account-hero-body">
          <p className="vc-account-name">{display}</p>
          {user?.email && <p className="vc-account-email">{user.email}</p>}
          {!isGuest && !editingProfile ? (
            <button type="button" className="vc-account-edit-link" onClick={() => setEditingProfile(true)}>
              {t('account.editProfile')}
            </button>
          ) : null}
          {!isGuest && editingProfile ? (
            <div className="vc-account-edit-form">
              <label className="vc-account-field">
                <span className="vc-account-field__label">{t('account.editName')}</span>
                <input
                  type="text"
                  className="vc-account-field__input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoComplete="name"
                />
              </label>
              <label className="vc-account-field">
                <span className="vc-account-field__label">{t('account.editPhone')}</span>
                <input
                  type="tel"
                  className="vc-account-field__input"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  autoComplete="tel"
                />
              </label>
              <div ref={profileCitiesRef}>
                <SellerCitiesPicker
                  value={editCities}
                  onChange={setEditCities}
                  options={cityOptions}
                  disabled={profileBusy}
                />
              </div>
              <div className="vc-account-edit-actions">
                <button
                  type="button"
                  className="vc-account-edit-btn vc-account-edit-btn--ghost"
                  onClick={() => setEditingProfile(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="vc-account-edit-btn vc-account-edit-btn--primary"
                  disabled={profileBusy}
                  onClick={() => void saveProfileEdit()}
                >
                  {profileBusy ? '…' : t('account.save')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isGuest ? (
        <div className="vc-account-guest-card">
          <h3>{t('account.guestPreviewTitle')}</h3>
          <p className="meta-text">{t('account.guestPreviewHint')}</p>
          <ul className="vc-account-guest-list">
            <li>{t('vc.profile.buying')} 🔒</li>
            <li>{t('vc.profile.selling')} 🔒</li>
            <li>{t('vc.profile.settings')} 🔒</li>
          </ul>
          <button type="button" className="vc-connect-btn" onClick={onLogin}>
            {t('auth.login')}
          </button>
          <div className="vc-account-lang-block">
            <span className="vc-account-lang-label">{t('menu.language')}</span>
            <LanguageSelect
              value={language}
              onChange={onLanguage}
              className="vc-account-lang-select"
            />
          </div>
        </div>
      ) : null}

      {!isGuest ? (
        <div className="vc-account-status-card">
          <div className="vc-account-status-head">
            <strong>{t('account.statusTitle')}</strong>
            <span>{t('account.statusSteps', { done: setupDone, total: setupTotal })}</span>
          </div>
          <div className="vc-account-progress">
            <div className="vc-account-progress-fill" style={{ width: `${(setupDone / setupTotal) * 100}%` }} />
          </div>
          {!connectLoading ? (
            <ul className="vc-account-status-list">
              <li>{verifyDone ? '✓' : '○'} {t('sellerVerify.sectionTitle')}</li>
              {PAYMENTS_ENABLED ? (
                <>
                  <li>{stripeDone ? '✓' : '○'} Stripe</li>
                  <li>{passDone ? '✓' : '○'} Exchange Pass</li>
                </>
              ) : null}
            </ul>
          ) : (
            <p className="meta-text">{t('common.loading')}</p>
          )}
          {setupDone < setupTotal ? (
            <button
              type="button"
              className="vc-account-status-cta"
              onClick={() => {
                setWizardStep(PAYMENTS_ENABLED ? (verifyDone ? (stripeDone ? 2 : 1) : 0) : 0)
                setWizardOpen(true)
              }}
            >
              {t('account.statusComplete')}
            </button>
          ) : null}
        </div>
      ) : null}

      {!isGuest ? (
        <div className="vc-account-tabs" role="tablist">
          {(
            [
              ['actions', t('account.tabActions')],
              ['selling', t('account.tabSelling')],
              ['settings', t('account.tabSettings')],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              className={`vc-account-tab${tab === key ? ' is-active' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {!isGuest && tab === 'actions' ? (
        <div key="actions" className="vc-account-panel">
          <div className="vc-account-card">
            <h3 className="vc-account-card__title">{t('vc.profile.buying')}</h3>
            {renderQuick(buyingActions)}
          </div>
          <div className="vc-account-card">
            <h3 className="vc-account-card__title">{t('vc.profile.selling')}</h3>
            {renderQuick(sellingActions)}
          </div>
          {listingsCount === 0 && onSell ? (
            <div className="vc-account-empty">
              <p>{t('account.listingsEmpty')}</p>
              <button type="button" className="vc-connect-btn" onClick={onSell}>
                {t('account.listingsEmptyCta')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {!isGuest && tab === 'selling' ? (
        <div key="selling" className="vc-account-panel">
        <div ref={verifySectionRef} className="vc-account-card">
          <h3 className="vc-account-card__title">{t('account.tabSelling')}</h3>
          <p>{verifyDone ? t('account.verifyDone') : t('account.verifyOpen')}</p>
          {PAYMENTS_ENABLED ? (
            <>
              <p className="meta-text">{stripeDone ? t('account.stripeDone') : t('account.stripePending')}</p>
              <p className="meta-text">{passDone ? t('account.passDone') : t('account.passPending')}</p>
            </>
          ) : null}
          <button
            type="button"
            className="vc-connect-btn"
            onClick={() => {
              setWizardStep(0)
              setWizardOpen(true)
            }}
          >
            {t('account.statusComplete')}
          </button>
        </div>
        </div>
      ) : null}

      {!isGuest && tab === 'settings' ? (
        <div key="settings" className="vc-account-panel">
          <div className="vc-account-card">
            <h3 className="vc-account-card__title">{t('menu.language')}</h3>
            <LanguageSelect
              value={language}
              onChange={onLanguage}
              className="vc-account-lang-select"
            />
          </div>
          <div className="vc-account-card vc-account-card--settings">
            <button
              type="button"
              className="vc-account-card__title vc-account-card__title--toggle"
              onClick={() => setSettingsOpen((v) => !v)}
              aria-expanded={settingsOpen}
            >
              <span className="vc-account-card__title-inner">
                <IconAccountSettings width={18} height={18} />
                {t('vc.profile.settings')}
              </span>
              <span className="vc-account-row__chevron" aria-hidden="true">
                {settingsOpen ? '⌃' : '›'}
              </span>
            </button>
            <CollapseSlot open={settingsOpen}>
              <div className="vc-account-settings-body">
                <label className="vc-account-toggle-row">
                  <span className="vc-account-toggle-row__text">
                    <span className="vc-account-toggle-row__label">{t('vc.profile.promoBanner')}</span>
                    <span className="vc-account-toggle-row__hint">{t('vc.profile.promoBannerHint')}</span>
                  </span>
                  <input
                    type="checkbox"
                    className="vc-account-toggle"
                    checked={promoBannerEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked
                      setPromoBannerEnabledState(enabled)
                      setPromoBannerEnabled(enabled)
                    }}
                    aria-label={t('vc.profile.promoBanner')}
                  />
                </label>
                <div className="vc-account-password">
                  <h4 className="vc-account-password__title">
                    {passwordLoginEnabled === false
                      ? t('account.passwordCreateTitle')
                      : t('account.passwordChangeTitle')}
                  </h4>
                  <p className="vc-account-password__hint">
                    {passwordLoginEnabled === false
                      ? t('account.passwordCreateHint')
                      : t('account.passwordChangeHint')}
                  </p>
                  {passwordLoginEnabled === null ? (
                    <p className="vc-account-password__hint">{t('common.loading')}</p>
                  ) : (
                  <form className="vc-account-password__form" onSubmit={(e) => void handleSetPassword(e)}>
                    {passwordLoginEnabled ? (
                      <label className="vc-account-password__field">
                        <span>{t('account.passwordCurrent')}</span>
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          autoComplete="current-password"
                          required
                        />
                      </label>
                    ) : null}
                    <label className="vc-account-password__field">
                      <span>{t('auth.resetPasswordNew')}</span>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        minLength={8}
                        required
                      />
                    </label>
                    <label className="vc-account-password__field">
                      <span>{t('auth.resetPasswordConfirm')}</span>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                        minLength={8}
                        required
                      />
                    </label>
                    {passwordError ? (
                      <p className="vc-account-password__error" role="alert">
                        {passwordError}
                      </p>
                    ) : null}
                    {passwordMessage ? (
                      <p className="vc-account-password__success" role="status">
                        {passwordMessage}
                      </p>
                    ) : null}
                    <button type="submit" className="vc-account-password__btn" disabled={passwordBusy}>
                      {passwordBusy
                        ? t('common.loading')
                        : passwordLoginEnabled === false
                          ? t('account.passwordCreateSubmit')
                          : t('account.passwordChangeSubmit')}
                    </button>
                  </form>
                  )}
                </div>
                <LegalAccordion onOpenLegal={onOpenLegal} />
              </div>
            </CollapseSlot>
          </div>
          {isAdmin ? (
            <div className="vc-account-card">
              <button type="button" className="vc-account-row" onClick={onAdmin}>
                <span className="vc-account-row__text">{t('menu.admin')}</span>
                <span className="vc-account-row__chevron">›</span>
              </button>
            </div>
          ) : null}
          <button type="button" className="vc-account-logout" onClick={onLogout}>
            {t('menu.logout')}
          </button>
        </div>
      ) : null}

      {token ? (
        <SellerOnboardingWizard
          open={wizardOpen}
          token={token}
          defaultFullName={user?.full_name ?? ''}
          initialStep={wizardStep}
          onClose={() => setWizardOpen(false)}
          onProgress={loadStatus}
        />
      ) : null}
    </section>
  )
}
